import { Autocomplete, TextField } from '@mui/material';
import useHelperTextId from '@platform-ui-common/hooks/contexts/useHelperTextId.js';
import useThrottle from '@platform-ui-common/hooks/useThrottle.js';
import { getLabelProps } from '@platform-ui/utilities/application/utils.js';
import cx from 'classnames';
import { HTMLAttributes, ReactNode, useEffect, useState } from 'react';

interface AutocompleteInputProps<Option> {
  disabled?: boolean;
  error?: string | boolean;
  fullWidth?: boolean;
  getOptionLabel?: (option: Option) => string;
  id: string;
  label: string;
  loading?: boolean;
  loadingComponent?: ReactNode;
  onChange: (option?: Option) => void;
  options: Option[];
  placeholder?: ReactNode;
  readOnly?: boolean;
  renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: Option) => JSX.Element;
  required?: boolean;
  search: (value: string, type?: 'house' | 'city') => void;
  value?: NonNullable<Option>;
}

export default function AutocompleteInput<Option extends { id: string }>({
  disabled,
  error,
  fullWidth,
  getOptionLabel,
  id,
  loading = false,
  loadingComponent,
  onChange,
  options,
  placeholder,
  readOnly = false,
  renderOption,
  required,
  search,
  value,
  ...textFieldProps
}: AutocompleteInputProps<Option>): JSX.Element {
  const [inputValue, setInputValue] = useState('');

  const throttledSearch = useThrottle(search);
  useEffect(() => {
    if (inputValue !== '') throttledSearch(inputValue);
    return throttledSearch.clear;
  }, [inputValue, throttledSearch]);

  const helperTextId = useHelperTextId();

  return (
    <Autocomplete
      id={id}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, val): boolean => option.id === val?.id}
      options={[...(value ? [value] : []), ...options]}
      autoComplete
      forcePopupIcon={false}
      disableClearable
      disabled={disabled}
      readOnly={readOnly}
      includeInputInList
      filterSelectedOptions
      value={value ?? null}
      noOptionsText={inputValue ? undefined : placeholder}
      onChange={(_, newValue: NonNullable<Option>): void => {
        onChange(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue): void => {
        if (newInputValue === '' && !required) onChange();
        setInputValue(newInputValue);
      }}
      renderInput={(params): JSX.Element => (
        <div className={cx('max-w-full', !fullWidth && 'w-field')}>
          <TextField
            {...params}
            {...textFieldProps}
            inputProps={{
              ...params.inputProps,
              'aria-errormessage': helperTextId,
              'aria-describedby': helperTextId,
            }}
            FormHelperTextProps={{
              id: helperTextId,
            }}
            disabled={disabled}
            InputLabelProps={getLabelProps(readOnly, value)}
            variant="filled"
            autoComplete="off"
            required={required}
            error={!!error}
            helperText={typeof error === 'string' ? error : undefined}
          />
        </div>
      )}
      renderOption={
        renderOption ??
        ((props, option): JSX.Element => (
          <li {...props} key={option.id}>
            {getOptionLabel ? getOptionLabel(option) : option.id}
          </li>
        ))
      }
      filterOptions={(x): Option[] => x}
      loading={loading}
      loadingText={loadingComponent}
    />
  );
}
